import Dexie from "dexie"

// Single Dexie instance for the WIN Field App offline store.
// Import this module from any Stimulus controller that needs offline storage.
//
// Tables:
//   pending_responses  -- queued response form submissions (new + updates)
//   pending_results    -- queued mission task result updates
//   pending_images     -- queued image uploads (metadata; legacy rows also carry an
//                         embedded blob, new photos reference pending_blobs by blobKey)
//   pending_blobs      -- photo bytes, written once and never rewritten (card #3995)
//   sync_log           -- record of sync attempts and outcomes
//   pending_cards    -- Raise a Ticket submissions queued for the server (#2617)

const db = new Dexie("FieldDb")

db.version(1).stores({
  // Response submissions queued while offline.
  // queuedAt: ISO timestamp. status: 'pending' | 'syncing' | 'failed'.
  pending_responses: "++id, responseId, status, queuedAt",

  // Individual mission task result updates queued while offline.
  // Separate from pending_responses so partial saves are possible.
  pending_results: "++id, responseId, missionTaskId, status, queuedAt",

  // Image upload queue. blob stored inline; blobKey is the local reference.
  // status: 'pending' | 'uploading' | 'failed'
  // uploadedAt: set when confirmed by server (record purged immediately after)
  pending_images: "++id, blobKey, surveyableId, status, queuedAt, uploadedAt",

  // Sync attempt log for debugging and monitoring.
  // kind: 'responses' | 'results' | 'images' | 'full'
  // outcome: 'success' | 'partial' | 'failed'
  sync_log: "++id, kind, outcome, syncedAt"
})

db.version(2).stores({
  // Read-side cache populated by the prefetch API.
  // Stores all data needed to render and complete a survey while offline.
  // responseId: server Response#id. placeId, scheduledAt: for querying.
  // cachedAt: ISO timestamp of last prefetch download.
  cached_responses: "++id, responseId, placeId, scheduledAt, cachedAt"
})

db.version(3).stores({
  // Normalised place cache. Keyed by placeId (server Place#id).
  // One record per place -- eliminates the inline place object that was
  // duplicated on every cached_responses record.
  //
  // Non-indexed fields added since the direct-upload revision (no version bump
  // needed -- Dexie stores them transparently): clientId / clientName, the
  // client of the owning response's mission chain, populated by the prefetch
  // and sync writers to drive the My Photos direct-upload Place/Client form.
  cached_places: "placeId"
}).upgrade(tx =>
  // Back-populate cached_places from any existing cached_responses records
  // so the first load after deploy doesn't show an empty places view.
  tx.table("cached_responses").toCollection().modify(r => {
    if (r.place?.id) {
      tx.table("cached_places").put({
        placeId:          r.place.id,
        name:             r.place.name,
        latitude:         r.place.latitude,
        longitude:        r.place.longitude,
        address:          r.place.address,
        bad_connectivity: r.place.bad_connectivity || false
      })
      delete r.place
    }
  })
)

db.version(4).stores({
  // In-app diagnostic log. Rolling window, capped at 500 entries.
  // level: 'error' | 'warn' | 'info' | 'event'
  // category: 'network' | 'sync' | 'upload' | 'survey' | 'sw' | 'app' | 'battery'
  field_log: "++id, level, category, timestamp"
})

db.version(5).stores({
  // Renamed field_log -> app_log for shared PWA logging.
  field_log: null,
  app_log: "++id, level, category, timestamp"
}).upgrade(tx =>
  tx.table("field_log").toCollection().modify(function () {
    tx.table("app_log").add(this.value)
    delete this.value
  })
)

db.version(6).stores({
  // Session key-value store. Used to share page-context data (e.g. CSRF token)
  // with the service worker, which cannot access document or localStorage.
  session: "key"
})

db.version(7).stores({
  // Image vault (ADR-0048, W1/W2). pending_images now banks EVERY capture --
  // both offline-queued captures and online "shadow" backups -- so there is
  // always a local recovery copy until the whole response is confirmed synced.
  //
  // New indexed fields:
  //   responseId -- the parent Response#id, so shadow rows can be purged once
  //                 their response is confirmed synced (W2 point 3).
  //   kind       -- "queued" (offline capture, the only copy, NEVER time-purged)
  //                 vs "shadow" (online capture, server already has it, the local
  //                 copy is a safety net). Drives the pending-safe stale sweep.
  //
  // Non-indexed fields also added by queueImage: placeId, clientId, questionName,
  // serverUrl. Existing v1-v6 records lack these; the upgrade backfills kind.
  // submittedAt (non-indexed) is stamped by purgeForResponse when a response
  // containing an already-uploaded queued capture is submitted; stamped rows are
  // swept by purgeSubmitted after a 24h grace.
  pending_images: "++id, blobKey, surveyableId, responseId, kind, status, queuedAt, uploadedAt"
}).upgrade(tx =>
  // Existing records predate the kind discriminator. They were all written by
  // the offline queue path, so they are "queued" captures and must be treated
  // as pending-safe (never time-purged) per ADR-0048 §3.
  tx.table("pending_images").toCollection().modify(r => {
    if (r.kind === undefined) r.kind = "queued"
  })
)

db.version(8).stores({
  // Raise a Ticket queue (card #2617). Every submission is written here FIRST,
  // then flushed by field_card_sync.js -- immediately when online, else from
  // the regular sync cycle -- so a ticket raised with no signal is never lost.
  //
  //   clientId -- uuid generated on the device; the server dedupes replays on it
  //   status   -- 'pending' | 'reauth-required' | 'sent' | 'failed'
  //   queuedAt -- ISO timestamp of the tap on Submit
  //
  // Non-indexed: hubId (null on rows queued before the Hub select -- the
  // server falls back to General IT; non-indexed, so it needed no version
  // bump), categoryTags (array of tag names; older rows have a single
  // category slug instead), body, context ({ placeId, missionId, responseId,
  // platformOs, displayMode, appVersion }), photo (compressed Blob or null),
  // sentAt, cardId, cardName, error. Sent rows keep their text but drop the
  // photo blob so the queue doesn't hoard storage.
  pending_cards: "++id, &clientId, status, queuedAt"
})

db.version(9).stores({
  // Photo vault v2 (card #3995). One row per photo, written ONCE at capture and
  // never touched again: no status change goes near it.
  //
  //   blobKey    -- the capture id, the same value as pending_images.blobKey
  //   bytes      -- the photo as an ArrayBuffer (not a Blob). On iOS a Blob held
  //                 in IndexedDB is readable after the process that wrote it has
  //                 died, but rewriting the ROW that holds it makes it unreadable;
  //                 plain bytes in a row of their own sidestep that.
  //   type       -- MIME type
  //   sha256     -- hex digest taken before the write, checked on every read
  //   byteLength -- length taken before the write, checked on every read
  //   createdAt  -- ISO timestamp
  //
  // No upgrade function: existing pending_images rows (with their embedded
  // blob) are left exactly as they are and are read through the legacy path.
  pending_blobs: "blobKey"
})

db.version(10).stores({
  // Personal settings outbox (card #4153), flushed by field_settings_sync.js.
  // One row per setting key, so a second change before the first has synced
  // replaces it: only the newest value is worth sending.
  //
  //   key       -- the setting, e.g. "battery_monitoring"
  //   value     -- the Tasker's new value (true / false)
  //   changedAt -- ISO timestamp of the change; the server keeps the newest per key
  //   queuedAt  -- ISO timestamp the row was written
  pending_settings: "key"
})

db.version(11).stores({
  // Server-write ledger (card #4688). One row per attempted server write on a
  // response -- start, complete, schedule, answers sync, photo link -- with
  // the action, this device's local status before/after, the HTTP status,
  // the server-returned status (when the response exposes it -- never
  // available on a redirecting action followed to completion, see
  // field_response_ledger.js), and the request's X-Request-Id. Written and
  // trimmed by field_response_ledger.js, which caps each response at a small
  // number of recent rows -- this is a short recent trail for support
  // tracing and the "status reverted" check, never a full history. No
  // answers, photo bytes or personal data ever enters a row.
  response_ledger: "++id, responseId, loggedAt"
})

db.version(12).stores({
  // Server photo delete tombstones (card #4744). A tasker's photo delete
  // ALWAYS removes the local/queued copy immediately (removePhotoFromQuestion
  // -> discardByUrls), but if the photo already has a server copy and the
  // device is offline (or the DELETE request itself fails), the server-side
  // half is queued here and replayed by field_sync_controller on reconnect --
  // same shape as pending_cards. Keyed by url (not ++id): a second delete
  // attempt on the same url before the first has synced just overwrites the
  // one row, so a retry can never double-queue the same tombstone.
  pending_photo_deletes: "url, queuedAt"
})

// `field:queue-changed` (card #3863). Fired on window after any write to the
// photo or survey queue commits: an enqueue, a status change, a purge. The
// sync queue bar listens for it, so a photo captured offline shows on the bar
// straight away -- no sync event fires for that, and without this the bar
// would only catch up on the next navigation.
//
// Hooked here rather than at each call site because the writers are spread
// across field_image_queue.js, field_upload_sync.js and the result editor.
// Writes in one tick coalesce into one event.
//
// Cost: in Dexie 4, registering "updating" and "deleting" hooks makes the
// hooks middleware read each existing row before its put or delete, and a
// Collection.delete() then runs row by row instead of as one range delete.
// That is acceptable here: these two queues hold tens of rows at most (one
// tasker's unsent work), so the extra reads are a few small IndexedDB gets
// per write. Do not copy this pattern onto a large table.
//
// Page context only: the service worker has no window, so nothing is hooked
// there. The worker has its own Dexie connection, so its writes never fire
// these page hooks either; instead it posts { type: "QUEUE_CHANGED" } to its
// window clients when a background pass ends, and application.js turns that
// into the same field:queue-changed event.
//
// Nothing here may throw: a hook that throws aborts the caller's transaction.
if (typeof window !== "undefined") {
  let queued = false
  const notify = () => {
    if (queued) return
    queued = true
    setTimeout(() => {
      queued = false
      try { window.dispatchEvent(new CustomEvent("field:queue-changed")) } catch { /* never block a write */ }
    }, 0)
  }
  const onCommit = (transaction) => {
    try { transaction?.on?.("complete", notify) } catch { /* never block a write */ }
  }

  for (const table of [db.pending_images, db.pending_responses]) {
    table.hook("creating", (_primKey, _obj, transaction) => { onCommit(transaction) })
    table.hook("updating", (_mods, _primKey, _obj, transaction) => { onCommit(transaction) })
    table.hook("deleting", (_primKey, _obj, transaction) => { onCommit(transaction) })
  }
}

export default db
